import type { ThemeRow, ThemeTokens } from "@journal/shared";

const MIN_REQUIRED_GLOWS = 3;

const isStringArray = (arr: unknown): arr is string[] =>
  Array.isArray(arr) && arr.every((item) => typeof item === "string");

const parseGlowColors = (raw: string | null | undefined): string[] | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isStringArray(parsed) && parsed.length > 0) {
      return parsed;
    }
  } catch {
    // Ignore parsing errors for invalid raw JSON strings, falling back safely to null
  }
  return null;
};

/** Glows of the global theme (Journal has no per-list themes). */
export const getGlowColors = (globalTheme: ThemeRow | null, globalToks: ThemeTokens | null) => {
  if (globalTheme) {
    const glowArray = parseGlowColors(globalTheme.glow_colors);
    if (glowArray && glowArray.length >= MIN_REQUIRED_GLOWS) {
      return {
        glow0: glowArray[0],
        glow1: glowArray[1],
        glow2: glowArray[2],
      };
    }
  }

  if (globalToks && globalToks.id !== "none") {
    return {
      glow0: globalToks.glow0,
      glow1: globalToks.glow1,
      glow2: globalToks.glow2,
    };
  }

  return { glow0: "var(--glow-0)", glow1: "var(--glow-1)", glow2: "var(--glow-2)" };
};

/**
 * Stable key for the page-entry animation. It re-triggers the slide-up+fade
 * when the user moves to a *different section*, but NOT when only a route param
 * changes within the same section. This is the contract that lets the day and
 * calendar carousels page between dates/months (which navigate `/day/$date`)
 * without the page-level entry re-firing on every swipe.
 *
 * Grouping:
 *   "/" and "/day/<date>"            → "day"   (Today + day carousel share it)
 *   "/profile", "/profile/fonts", …  → "profile"
 *   "/admin", "/admin/themes", …     → "admin"
 *   "/organizations", "/organizations/<id>" → "organizations"
 *   everything else                  → its first path segment ("calendar", …)
 */
export const pageEntryKey = (pathname: string): string => {
  if (pathname === "/" || pathname.startsWith("/day/")) {
    return "day";
  }
  // First non-empty path segment. Sub-routes (e.g. /profile/fonts, /admin/themes,
  // /organizations/<id>) collapse onto their parent so navigating *into* a
  // settings detail keeps the same section shell and doesn't re-animate.
  const segment = pathname.split("/").find(Boolean);
  return segment ?? "day";
};
